import React, { useCallback } from 'react';
import {
  type Href,
  type RoutePath,
  type RouteParams,
  type RouteSegments,
  useNavigationContainerRef,
  usePathname,
  useRouter,
} from 'expo-router';

import type { AddressType } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { useDeliveryOrderInFlight } from '../useDeliveryOrderInFlight';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToMenu = (params: Params = {}) => {
  const { backToPath, isInsideModal } = params;

  const router = useRouter();
  const pathname = usePathname();
  const navigationRef = useNavigationContainerRef();

  const shouldResetMenu = Boolean(isInsideModal) || pathname.includes('/locations');
  const shouldDismissAll = isInsideModal || !pathname.includes('/locations');

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const resetToMenu = useCallback(
    (menuRoute: MenuRoute) => {
      const { screenName, ...href } = menuRoute;
      const shouldReturnToSelectedMenu =
        typeof backToPath === 'string' && backToPath.endsWith('/menu');
      const returnHref = shouldReturnToSelectedMenu ? href : (backToPath ?? href);

      const rootState = navigationRef.getRootState();
      const menuNavigationKey = findNavigationStateKey({
        navigationState: rootState,
        routeName: MENU_TAB_ROUTE,
      });

      if (!menuNavigationKey) {
        router.dismissTo(returnHref);

        return;
      }

      const resetAction = {
        type: 'RESET',
        target: menuNavigationKey,
        payload: {
          index: 0,
          routes: [{ name: screenName, params: href.params }],
        },
      };

      navigationRef.dispatch(resetAction as never);
      router.dismissTo(returnHref);
    },
    [backToPath, navigationRef, router],
  );

  const handleGoToMenu = React.useCallback(
    async (restaurantSlug: string, category?: string) => {
      const canDismiss = shouldDismissAll ? router.canDismiss() : false;

      if (shouldResetMenu) {
        resetToMenu({
          ...MENU_ROUTES.pickupOrOutpost,
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });

        return;
      }

      if (shouldDismissAll && canDismiss) {
        router.dismissAll();
      }

      const navigateToMenu = () => {
        router.navigate({
          pathname: MENU_ROUTES.pickupOrOutpost.pathname,
          params: { restaurantSlug, ...(category ? { category } : {}) },
        });
      };

      navigateToMenu();
    },
    [resetToMenu, shouldDismissAll, shouldResetMenu, router],
  );

  const handleGoToDeliveryMenu = React.useCallback(
    async (addressId: string, category?: string) => {
      const canDismiss = shouldDismissAll ? router.canDismiss() : false;

      if (shouldResetMenu) {
        resetToMenu({
          ...MENU_ROUTES.delivery,
          params: { addressId, ...(category ? { category } : {}) },
        });

        return;
      }

      if (shouldDismissAll && canDismiss) {
        router.dismissAll();
      }

      const navigateToDeliveryMenu = () => {
        router.navigate({
          pathname: MENU_ROUTES.delivery.pathname,
          params: { addressId, ...(category ? { category } : {}) },
        });
      };

      navigateToDeliveryMenu();
    },
    [resetToMenu, shouldDismissAll, shouldResetMenu, router],
  );

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  return useCallback(
    ({ orderType, restaurantSlug = '', deliveryOrderDetail, category }: UseNavigateToMenuProps) => {
      if (['pickup', 'outpost'].includes(orderType)) {
        void handleGoToMenu(restaurantSlug, category);

        return;
      }

      // Block going to delivery menu when there is an in-flight delivery order.
      if (inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const deliveryType = deliveryOrderDetail as Readonly<{
        address: { id: string };
      }>;
      const addressId = deliveryOrderDetail?.addressId ?? deliveryType?.address?.id;

      if (orderType === 'delivery' && addressId) {
        void handleGoToDeliveryMenu(addressId, category);
      }
    },
    [
      inFlightDeliveryOrderId,
      handleGoToDeliveryMenu,
      handleGoToMenu,
      navigateToDeliveryOrderInFlightScreen,
    ],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function findNavigationStateKey(params: FindNavigationStateKeyParams): string | undefined {
  const { navigationState, routeName } = params;

  const matchingRoute = navigationState.routes.find((route) => route.name === routeName);
  const matchingNavigationKey = matchingRoute?.state?.key;

  if (matchingNavigationKey) {
    return matchingNavigationKey;
  }

  return navigationState.routes.reduce<string | undefined>((navigationKey, route) => {
    if (navigationKey || !route.state) return navigationKey;

    return findNavigationStateKey({ navigationState: route.state, routeName });
  }, undefined);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MENU_TAB_ROUTE = '(menu)' satisfies RouteSegment;

const MENU_ROUTES = {
  delivery: {
    pathname: '/delivery/[addressId]/menu',
    screenName: 'delivery/[addressId]/menu/index',
  },
  pickupOrOutpost: {
    pathname: '/[restaurantSlug]/menu',
    screenName: '[restaurantSlug]/menu/index',
  },
} as const satisfies Record<string, MenuRouteDefinition>;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  backToPath?: Href;
  isInsideModal?: boolean;
};

type MenuRoute = {
  [Key in MenuRouteKey]: Extract<Href, object> &
    (typeof MENU_ROUTES)[Key] & {
      params: RouteParams<(typeof MENU_ROUTES)[Key]['pathname']>;
    };
}[MenuRouteKey];

type MenuRouteKey = keyof typeof MENU_ROUTES;

type MenuRouteDefinition = {
  pathname: RoutePath;
  screenName: StackRouteName<RoutePath>;
};

type NavigationState = {
  key?: string;
  routes: readonly NavigationRoute[];
};

type NavigationRoute = {
  name: string;
  state?: NavigationState;
};

type FindNavigationStateKeyParams = {
  navigationState: NavigationState;
  routeName: RouteSegment;
};

type RouteSegment = RouteSegments<RoutePath>[number];

type StackRouteName<Route extends RoutePath> = Route extends `/${infer Path}`
  ? `${Path}/index`
  : never;

export type UseNavigateToMenuProps = Readonly<{
  orderType: AddressType;
  restaurantId?: string;
  restaurantSlug?: string;
  restaurantName?: string;
  deliveryAddressName?: string;
  deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  referrer?: MenuScreenReferrer;
  category?: string;
}>;

type MenuScreenReferrer = 'bag';
